import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import SafeIcon from '../components/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { sendEmail } from '../services/emailService';

const {
  FiShield, FiUserCheck, FiActivity, FiLock, FiCpu,
  FiCheck, FiStar, FiPhone, FiAlertCircle
} = FiIcons;

const IMAGES = {
  hero:     "https://cdn.midjourney.com/56e1dace-4bb8-47cf-8491-be90076512f9/0_1.png",
  gate:     "https://cdn.midjourney.com/9be5e9df-c336-4036-98be-961612fd4447/0_1.png",
  wisdom:   "https://cdn.midjourney.com/2d437e25-d280-433e-b336-216ebeed8f03/0_3.png",
  tech:     "https://cdn.midjourney.com/c6318a86-2504-481b-ae86-ed0c33b178c5/0_2.png",
  garden:   "https://cdn.midjourney.com/4cfbd547-af47-4611-ba1a-1ba9627860dd/0_1.png",
  statue:   "https://cdn.midjourney.com/46f87f5d-5fd9-4541-97ed-1a22ac2334d9/0_0.png",
  path:     "https://cdn.midjourney.com/5a7bfe1a-e61b-4322-9dac-79bf7b2e64ea/0_0.png",
  ethereal: "https://cdn.midjourney.com/6b3527d6-d4be-4880-aeb6-26f1b039c873/0_0.png"
};

const INPUT_CLS =
  "w-full p-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-1 focus:ring-charcoal focus:border-charcoal transition-all text-sm";

export default function GatekeeperAI() {
  const [formState, setFormState] = useState({
    name: '', email: '', phone: '',
    role: 'creator', level: 'seed', intent: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted]   = useState(false);
  const [submitError, setSubmitError]   = useState('');

  const set = (field) => (e) =>
    setFormState((p) => ({ ...p, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    try {
      await sendEmail({
        name:  formState.name,
        email: formState.email,
        phone: formState.phone,
        role:  formState.role,
        tier:  formState.level,
        goal:  formState.intent,
      });
      setIsSubmitted(true);
    } catch (err) {
      console.error('EmailJS error:', err);
      setSubmitError('Could not deliver email. Please try again or contact us directly.');
      setIsSubmitting(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollTo = (id) => () =>
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  const fadeInUp = {
    hidden:  { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } }
  };

  return (
    <div className="bg-beige min-h-screen font-dm-sans text-charcoal overflow-x-hidden">

      {/* ── 1. HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative h-screen min-h-[700px] w-full flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={IMAGES.hero} alt="Gatekeeper Sanctuary" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-charcoal/40 mix-blend-multiply" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center text-white space-y-8 pt-20">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-xs uppercase tracking-widest font-bold mb-8">
              <SafeIcon icon={FiShield} className="w-3 h-3 text-sage" />
              <span>Managed Intelligence Layer</span>
            </div>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-cormorant font-bold leading-[0.95] tracking-tight mb-6">
              Every Garden<br />Needs a Gate.
            </h1>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="text-lg md:text-2xl text-white/90 leading-relaxed max-w-2xl mx-auto font-light"
          >
            Gatekeeper AI is a supervised intelligence that guides visitors, clarifies intent, and protects focus inside MindGarden.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8"
          >
            <Button variant="primary" size="lg" onClick={scrollTo('inquiry-section')}
              className="bg-sage text-white hover:bg-moss min-w-[200px] h-14 text-lg border-none shadow-xl shadow-black/20">
              Request Access
            </Button>
            <Button variant="ghost" size="lg" onClick={scrollTo('problem-section')}
              className="text-white hover:bg-white/10 border border-white/30 min-w-[200px] h-14 text-lg backdrop-blur-sm">
              How It Works
            </Button>
          </motion.div>
        </div>
      </section>

      {/* ── 2. PROBLEM ──────────────────────────────────────────────────────── */}
      <section id="problem-section" className="py-24 lg:py-32 px-6 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.8 }}
            className="order-2 lg:order-1 relative"
          >
            <div className="relative h-[600px] rounded-[2rem] overflow-hidden shadow-2xl">
              <img src={IMAGES.statue} alt="Stoic Guardian" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-transparent to-transparent" />
              <div className="absolute bottom-12 left-12 right-12 text-white">
                <p className="font-cormorant text-3xl md:text-4xl italic leading-tight">"Silence is the fence around wisdom."</p>
              </div>
            </div>
            <div className="absolute -z-10 top-10 -left-10 w-full h-full border-2 border-sage/20 rounded-[2rem]" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.8 }}
            className="space-y-12 order-1 lg:order-2"
          >
            <div>
              <h2 className="text-4xl md:text-5xl font-cormorant font-bold text-charcoal leading-tight mb-6">
                Attention Without Intention Creates Noise.
              </h2>
              <p className="text-xl text-charcoal/60 leading-relaxed">
                Modern platforms force a choice between total isolation or overwhelming noise. MindGarden exists to resolve this tension.
              </p>
            </div>
            <div className="space-y-6">
              {[
                { title: "Open Access",        desc: "Drains focus and dilutes quality interactions." },
                { title: "Closed Access",       desc: "Kills opportunity and isolates growth." },
                { title: "Generic Automation",  desc: "Creates risk through impersonal responses." },
                { title: "Manual Control",      desc: "Doesn't scale with your success." }
              ].map((item, i) => (
                <div key={i} className="flex gap-6 group">
                  <div className="w-12 h-12 flex-shrink-0 border border-sage/30 rounded-full flex items-center justify-center text-sage font-cormorant font-bold text-xl group-hover:bg-sage group-hover:text-white transition-colors duration-300">
                    {i + 1}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-charcoal mb-1">{item.title}</h3>
                    <p className="text-charcoal/60 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── 3. SOLUTION ─────────────────────────────────────────────────────── */}
      <section className="py-24 lg:py-32 px-6 bg-charcoal text-beige relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <img src={IMAGES.tech} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-6xl font-cormorant font-bold text-white mb-6">Meet Gatekeeper AI.</h2>
            <p className="text-xl text-white/70 leading-relaxed font-light">
              It is not a chatbot. It is not raw automation.<br />
              It is a managed steward that combines machine speed with human judgment.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: FiCpu,      title: "AI Handles Volume",       desc: "Instant, courteous responses to every visitor, 24/7. No signal is lost, but no noise gets through.", color: "text-skyblue" },
              { icon: FiUserCheck,title: "Humans Ensure Alignment", desc: "Our stewardship team reviews interactions to refine tone and handle complex edge cases.", color: "text-sage" },
              { icon: FiShield,   title: "Intent Protected",        desc: "Only high-value, aligned opportunities are escalated to your attention. The rest are politely deflected.", color: "text-goldenrod" }
            ].map((f, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.2 }}
                className="bg-white/5 border border-white/10 p-8 rounded-2xl hover:bg-white/10 transition-colors duration-300"
              >
                <div className="w-14 h-14 rounded-xl bg-white/5 flex items-center justify-center mb-6 border border-white/10">
                  <SafeIcon icon={f.icon} className={`w-7 h-7 ${f.color}`} />
                </div>
                <h3 className="text-2xl font-cormorant font-bold text-white mb-4">{f.title}</h3>
                <p className="text-white/60 leading-relaxed font-dm-sans">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4. HOW IT WORKS ─────────────────────────────────────────────────── */}
      <section className="py-24 lg:py-32 px-6 bg-beige overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <h2 className="text-4xl md:text-5xl font-cormorant font-bold text-charcoal mb-6">The Flow of Stewardship</h2>
              <div className="h-1 w-20 bg-sage mb-6" />
              <p className="text-xl text-charcoal/70 leading-relaxed max-w-lg">
                Your garden remains peaceful while the Gatekeeper manages the perimeter.
              </p>
            </div>
            <div className="hidden lg:block relative h-px bg-sage/20 w-full">
              <div className="absolute right-0 -top-1.5 w-3 h-3 bg-sage rounded-full" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-12 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-sage/30 to-transparent z-0" />
            {[
              { step: "01", title: "Arrival",    desc: "A visitor arrives at your MindGarden public profile.", img: IMAGES.path },
              { step: "02", title: "Guidance",   desc: "Gatekeeper AI welcomes them and listens for their intent.", img: IMAGES.gate },
              { step: "03", title: "Refinement", desc: "Human supervision reviews signals and refines behavior.", img: IMAGES.wisdom }
            ].map((item, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.2 }}
                className="relative z-10"
              >
                <div className="aspect-[4/3] rounded-2xl overflow-hidden mb-8 shadow-md border border-white/50 group">
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-sage/10 relative">
                  <div className="absolute -top-6 left-6 w-12 h-12 bg-charcoal text-white flex items-center justify-center rounded-full font-cormorant font-bold text-xl border-4 border-beige">
                    {item.step}
                  </div>
                  <h3 className="text-2xl font-cormorant font-bold text-charcoal mb-2 mt-2">{item.title}</h3>
                  <p className="text-charcoal/60 leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5. STEWARDSHIP LEVELS ───────────────────────────────────────────── */}
      <section className="py-24 lg:py-32 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-cormorant font-bold text-charcoal mb-4">Stewardship Levels</h2>
            <p className="text-xl text-charcoal/60">Pricing reflects the level of human supervision and refinement your garden receives.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
            {/* Seed */}
            <motion.div whileHover={{ y: -5 }} transition={{ duration: 0.3 }}>
              <div className="p-8 rounded-3xl border border-sage/20 bg-beige/30 flex flex-col h-full">
                <div className="mb-6">
                  <h3 className="text-2xl font-cormorant font-bold text-charcoal">Seed</h3>
                  <p className="text-sm text-charcoal/50 mt-1">For growing gardens</p>
                </div>
                <div className="mb-8">
                  <span className="text-4xl font-dm-sans font-bold text-charcoal">$29</span>
                  <span className="text-charcoal/40">/mo</span>
                  <div className="text-xs text-charcoal/40 mt-1">or $299/year</div>
                </div>
                <ul className="space-y-4 mb-8 flex-grow">
                  {["Supervised AI guide","Guided visitor responses","Intent-based routing","Monthly human review"].map((f,i) => (
                    <li key={i} className="flex gap-3 text-sm text-charcoal/70">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-sage flex-shrink-0" />{f}
                    </li>
                  ))}
                </ul>
                <Button variant="secondary" onClick={() => { setFormState(p=>({...p,level:'seed'})); scrollTo('inquiry-section')(); }} className="w-full">
                  Select Seed
                </Button>
              </div>
            </motion.div>

            {/* Private */}
            <motion.div whileHover={{ y: -5 }} transition={{ duration: 0.3 }} className="relative md:-mt-8">
              <div className="absolute -top-4 inset-x-0 flex justify-center">
                <span className="bg-charcoal text-white text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wider">Recommended</span>
              </div>
              <div className="p-8 rounded-3xl border-2 border-charcoal bg-white shadow-2xl flex flex-col h-full relative z-10">
                <div className="mb-6">
                  <h3 className="text-3xl font-cormorant font-bold text-charcoal">Private</h3>
                  <p className="text-sm text-charcoal/50 mt-1">For high-value attention</p>
                </div>
                <div className="mb-8">
                  <span className="text-5xl font-dm-sans font-bold text-charcoal">$149</span>
                  <span className="text-charcoal/40">/mo</span>
                  <div className="text-xs text-charcoal/40 mt-1">or $1,500/year</div>
                </div>
                <ul className="space-y-4 mb-8 flex-grow">
                  {["Everything in Seed","Advanced intent filtering","Priority routing","Quiet noise deflection","Regular human refinement","Signal analysis insights"].map((f,i) => (
                    <li key={i} className="flex gap-3 text-sm text-charcoal font-medium">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-goldenrod flex-shrink-0" />{f}
                    </li>
                  ))}
                </ul>
                <Button variant="primary" onClick={() => { setFormState(p=>({...p,level:'private'})); scrollTo('inquiry-section')(); }} className="w-full bg-charcoal hover:bg-black text-white h-12">
                  Select Private
                </Button>
                <div className="mt-4 text-center text-xs text-charcoal/40">Optional setup: $750 (one-time)</div>
              </div>
            </motion.div>

            {/* Concierge */}
            <motion.div whileHover={{ y: -5 }} transition={{ duration: 0.3 }}>
              <div className="p-8 rounded-3xl border border-sage/20 bg-beige/30 flex flex-col h-full relative overflow-hidden">
                <div className="absolute top-0 right-0 p-12 opacity-5">
                  <SafeIcon icon={FiStar} className="w-24 h-24 text-charcoal" />
                </div>
                <div className="mb-6 relative z-10">
                  <h3 className="text-2xl font-cormorant font-bold text-charcoal">Concierge</h3>
                  <p className="text-sm text-charcoal/50 mt-1">Dedicated oversight</p>
                </div>
                <div className="mb-8 relative z-10">
                  <span className="text-4xl font-dm-sans font-bold text-charcoal">From $500</span>
                  <span className="text-charcoal/40">/mo</span>
                  <div className="text-xs text-charcoal/40 mt-1">Limited availability</div>
                </div>
                <ul className="space-y-4 mb-8 flex-grow relative z-10">
                  {["Dedicated human supervision","Custom logic evolution","Priority refinement","White-glove management","Direct team access"].map((f,i) => (
                    <li key={i} className="flex gap-3 text-sm text-charcoal/70">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-sage flex-shrink-0" />{f}
                    </li>
                  ))}
                </ul>
                <Button variant="secondary" onClick={() => { setFormState(p=>({...p,level:'concierge'})); scrollTo('inquiry-section')(); }} className="w-full relative z-10">
                  Inquire
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── 6. INQUIRY FORM ─────────────────────────────────────────────────── */}
      <section id="inquiry-section" className="py-24 px-6 bg-sage/5">
        <div className="max-w-4xl mx-auto bg-white rounded-[2rem] shadow-xl overflow-hidden grid grid-cols-1 md:grid-cols-5">

          {/* Left panel */}
          <div className="md:col-span-2 bg-charcoal text-white p-10 flex flex-col justify-between relative overflow-hidden">
            <div className="absolute inset-0 opacity-20">
              <img src={IMAGES.garden} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="relative z-10 space-y-6">
              <div>
                <h2 className="text-3xl font-cormorant font-bold mb-3">Request Access</h2>
                <p className="text-white/70 text-sm leading-relaxed">
                  Submissions are reviewed by our stewardship team and delivered directly to our inbox.
                </p>
              </div>
              <div className="space-y-3 text-sm text-white/60">
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiCheck} className="w-4 h-4 text-sage" />
                  <span>Name, email & phone collected</span>
                </div>
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiCheck} className="w-4 h-4 text-sage" />
                  <span>Sent to evan.elefano@gmail.com</span>
                </div>
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiCheck} className="w-4 h-4 text-sage" />
                  <span>Response within 24 hours</span>
                </div>
              </div>
            </div>
            <div className="relative z-10 flex items-center gap-3 text-xs text-white/40 mt-8">
              <SafeIcon icon={FiLock} className="w-4 h-4" />
              <span>Secure & Confidential</span>
            </div>
          </div>

          {/* Right panel — form */}
          <div className="md:col-span-3 p-10">
            <AnimatePresence mode="wait">
              {isSubmitted ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="h-full flex flex-col items-center justify-center text-center space-y-6 py-8"
                >
                  <div className="w-20 h-20 bg-sage/10 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiCheck} className="w-10 h-10 text-sage" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-cormorant font-bold text-charcoal">Inquiry Received</h3>
                    <p className="text-charcoal/60 mt-2 text-sm">
                      Thank you, <strong>{formState.name}</strong>. We'll be in touch at <strong>{formState.email}</strong>.
                    </p>
                  </div>
                  <Button variant="secondary" onClick={() => { setIsSubmitted(false); setSubmitError(''); }}>
                    Submit Another
                  </Button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  onSubmit={handleSubmit}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="space-y-5"
                >
                  {/* Full Name */}
                  <div>
                    <label className="block text-xs font-bold text-charcoal/50 uppercase tracking-wider mb-1">
                      Full Name <span className="text-rose-400">*</span>
                    </label>
                    <input
                      type="text" required
                      value={formState.name} onChange={set('name')}
                      placeholder="Jane Doe"
                      className={INPUT_CLS}
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-xs font-bold text-charcoal/50 uppercase tracking-wider mb-1">
                      Email Address <span className="text-rose-400">*</span>
                    </label>
                    <input
                      type="email" required
                      value={formState.email} onChange={set('email')}
                      placeholder="jane@example.com"
                      className={INPUT_CLS}
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-xs font-bold text-charcoal/50 uppercase tracking-wider mb-1">
                      Phone Number <span className="text-rose-400">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                        <SafeIcon icon={FiPhone} className="w-4 h-4 text-charcoal/30" />
                      </div>
                      <input
                        type="tel" required
                        value={formState.phone} onChange={set('phone')}
                        placeholder="+1 (555) 000-0000"
                        className={`${INPUT_CLS} pl-9`}
                      />
                    </div>
                  </div>

                  {/* Role + Tier */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-charcoal/50 uppercase tracking-wider mb-1">Role</label>
                      <select value={formState.role} onChange={set('role')} className={INPUT_CLS}>
                        <option value="creator">Creator</option>
                        <option value="founder">Founder</option>
                        <option value="consultant">Consultant</option>
                        <option value="brand">Brand</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-charcoal/50 uppercase tracking-wider mb-1">Tier</label>
                      <select value={formState.level} onChange={set('level')} className={INPUT_CLS}>
                        <option value="seed">Seed — $29/mo</option>
                        <option value="private">Private — $149/mo</option>
                        <option value="concierge">Concierge — From $500</option>
                      </select>
                    </div>
                  </div>

                  {/* Primary Goal */}
                  <div>
                    <label className="block text-xs font-bold text-charcoal/50 uppercase tracking-wider mb-1">Primary Goal</label>
                    <input
                      type="text"
                      value={formState.intent} onChange={set('intent')}
                      placeholder="e.g. Filter inbound sales leads…"
                      className={INPUT_CLS}
                    />
                  </div>

                  {/* Error message */}
                  {submitError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex gap-2 text-xs text-red-700">
                      <SafeIcon icon={FiAlertCircle} className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>{submitError}</span>
                    </div>
                  )}

                  <Button
                    type="submit" variant="primary"
                    className="w-full bg-charcoal hover:bg-black text-white h-12 mt-2"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending Inquiry…' : 'Submit Inquiry →'}
                  </Button>

                  <p className="text-center text-xs text-charcoal/30">
                    Your information is kept private and never shared.
                  </p>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* ── 7. CLOSING ──────────────────────────────────────────────────────── */}
      <section className="relative py-32 text-center text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={IMAGES.ethereal} alt="Wisdom" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-charcoal/70" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 space-y-12">
          <div>
            <SafeIcon icon={FiActivity} className="w-12 h-12 text-sage mx-auto mb-6" />
            <h2 className="text-3xl md:text-5xl font-cormorant font-bold mb-6">Technology Should Serve Wisdom.</h2>
            <p className="text-xl text-white/80 font-light leading-relaxed">
              MindGarden does not believe in replacing judgment with machines.<br />We believe in amplifying it.
            </p>
          </div>
          <div className="pt-12 border-t border-white/10">
            <p className="text-2xl md:text-4xl font-cormorant italic text-white/90 mb-10 leading-tight">
              "Growth requires openness.<br />Wisdom requires boundaries.<br />Gatekeeper AI gives you both."
            </p>
            <Button variant="primary" size="lg" onClick={scrollTo('inquiry-section')}
              className="bg-sage text-white hover:bg-moss px-10 py-4 h-16 text-lg border-none shadow-2xl">
              Request Gatekeeper Access
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}